import moment from 'moment';
import { useMemo } from 'react';
import { DATE_RANGE_TYPES } from './useProcessedChartData';

export const useChartConfiguration = (selectedDateRange: string, processedChartData: any) => {
  const formatXAxisTick = useMemo(() => {
    return (value: number) => {
      const now = moment();
      let startDate: moment.Moment;

      if (selectedDateRange === DATE_RANGE_TYPES.last_month) {
        startDate = now.clone().subtract(1, 'month').startOf('day');
        const tickDate = startDate.clone().add(value, 'days');
        return tickDate.format('MMM-DD');
      } else if (selectedDateRange === DATE_RANGE_TYPES.last_6_month) {
        // For 6 months: show exactly 6 months from Feb to Aug (if current is Aug)
        startDate = now.clone().subtract(6, 'months').startOf('month');
        const tickDate = startDate.clone().add(value, 'months');
        return tickDate.format('MMM');
      } else {
        // For 1 year: show exactly 12 months
        startDate = now.clone().subtract(12, 'months').startOf('month');
        const tickDate = startDate.clone().add(value, 'months');
        return tickDate.format('MMM');
      }
    };
  }, [selectedDateRange]);

  const getStaticTicks = useMemo(() => {
    return () => {
      if (selectedDateRange === DATE_RANGE_TYPES.last_month) {
        // Show every 5th day for last month (0, 5, 10, 15, 20, 25, 30)
        const totalDays = processedChartData.totalDays || 30;
        const ticks = [];
        for (let i = 0; i <= totalDays; i += 5) {
          ticks.push(i);
        }
        return ticks;
      } else if (selectedDateRange === DATE_RANGE_TYPES.last_6_month) {
        // Show 7 month ticks for 6-month range (0, 1, 2, 3, 4, 5, 6) - Feb to Aug
        return [0, 1, 2, 3, 4, 5, 6];
      } else {
        // Show 13 month ticks for 1-year range (0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12)
        return [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];
      }
    };
  }, [selectedDateRange, processedChartData.totalDays]);

  const xAxisConfig = useMemo(() => {
    return {
      dataKey: 'name',
      type: 'number' as const,
      domain:
        selectedDateRange === DATE_RANGE_TYPES.last_month
          ? [0, processedChartData.totalDays || 30]
          : selectedDateRange === DATE_RANGE_TYPES.last_6_month
            ? [0, 6]
            : [0, 12],
      ticks: getStaticTicks(),
      tickFormatter: formatXAxisTick,
      axisLine: false,
      tickLine: false,
      tick: { fontSize: 12, fill: '#8c8c8c' },
      tickCount: 10,
      interval: 0,
      angle: selectedDateRange === DATE_RANGE_TYPES.last_month ? -45 : 0,
      textAnchor: selectedDateRange === DATE_RANGE_TYPES.last_month ? ('end' as const) : ('middle' as const),
      height: selectedDateRange === DATE_RANGE_TYPES.last_month ? 60 : 40,
    };
  }, [selectedDateRange, processedChartData.totalDays, getStaticTicks, formatXAxisTick]);

  const yAxisConfig = useMemo(() => {
    return {
      axisLine: false,
      tickLine: false,
      tick: { fontSize: 12, fill: '#8c8c8c' },
      tickFormatter: (value: number) => `€${value}`,
    };
  }, []);

  const lineConfig = useMemo(() => {
    return {
      type: 'monotone' as const,
      dataKey: 'price',
      stroke: '#05796B',
      strokeWidth: 2,
      connectNulls: true,
    };
  }, []);

  return {
    xAxisConfig,
    yAxisConfig,
    lineConfig,
    formatXAxisTick,
    getStaticTicks,
  };
};
